import React, { lazy } from 'react';
import RoleGuard from '../../components/auth/RoleGuard';
import AuthGuard from '../../components/auth/AuthGuard';
// Page components are code-split (lazy) so admin/instructor bundles are only
// downloaded when an admin actually navigates here. A <Suspense> boundary in
// AuthenticatedRoutes.jsx handles the loading state.
const UserManagement = lazy(() => import('../../pages/admin/users/UserManagement'));
const CourseManagement = lazy(() => import('../../pages/admin/courses/CourseManagement'));
const ElectiveManagement = lazy(() => import('../../pages/admin/electives/ElectiveManagement'));
const AdminDashboard = lazy(() => import('../../pages/admin/dashboard/AdminDashboard'));
const AdminAnnouncements = lazy(() => import('../../pages/admin/announcements/AdminAnnouncements'));
const StudentMessagesAdmin = lazy(() => import('../../pages/admin/studentMessages/StudentMessagesAdmin'));
const AdminExtensions = lazy(() => import('../../pages/admin/extensions/AdminExtensions'));
const ExtensionRequestWizard = lazy(() => import('../../pages/student/extensions/ExtensionRequestWizard'));
const ExtensionActionLanding = lazy(() => import('../../components/extensions/ExtensionActionLanding'));
const EnrollmentReports = lazy(() => import('../../pages/admin/reports/EnrollmentReports'));
const StudentGradesReport = lazy(() => import('../../pages/admin/reports/StudentGradesReport'));
const AttendanceManagement = lazy(() => import('../../pages/admin/attendance/AttendanceManagement'));
const AdminAttendanceCorrections = lazy(() => import('../../pages/admin/attendance/AdminAttendanceCorrections'));
const DeletedItemsManagement = lazy(() => import('../../pages/admin/deletedItems/DeletedItemsManagement'));
const ImportExport = lazy(() => import('../../pages/admin/importExport/ImportExport'));
const AdminPermissions = lazy(() => import('../../pages/admin/permissions/AdminPermissions'));
const EnrolledStudentsPage = lazy(() => import('../../pages/admin/courses/EnrolledStudentsPage'));
const CoursePricing = lazy(() => import('../../pages/admin/pricing/CoursePricing'));
const PromoCodeManagement = lazy(() => import('../../pages/admin/pricing/PromoCodeManagement'));
const BulkPricingManagement = lazy(() => import('../../pages/admin/pricing/BulkPricingManagement'));
const AdditionalProductsManagement = lazy(() => import('../../pages/admin/pricing/AdditionalProductsManagement'));
const PurchaseSearch = lazy(() => import('../../pages/admin/pricing/PurchaseSearch'));
const HlsBackfillPage = lazy(() => import('../../pages/admin/tools/HlsBackfillPage'));
const AdminSupportTicketList = lazy(() => import('../../pages/admin/supportTickets/AdminSupportTicketList'));
const AdminSupportTicketDetails = lazy(() => import('../../pages/admin/supportTickets/AdminSupportTicketDetails'));

// The /admin/* routes, plus /extensions/new which sits between
// /admin/reports and /admin/extensions in the original route table.
// Extracted VERBATIM (same order) from AuthenticatedRoutes' route table.
const adminRoutes = [
  { path: '/admin', element: (
    <RoleGuard allowedRoles={['admin']}>
      <AdminDashboard />
    </RoleGuard>
  ) },
  { path: '/admin/users', element: (
    <RoleGuard allowedRoles={['admin']}>
      <UserManagement />
    </RoleGuard>
  ) },
  { path: '/admin/courses', element: (
    <RoleGuard allowedRoles={['admin', 'instructor']}>
      <CourseManagement />
    </RoleGuard>
  ) },
  { path: '/admin/courses/:courseId/students', element: (
    <RoleGuard allowedRoles={['admin', 'instructor']}>
      <EnrolledStudentsPage />
    </RoleGuard>
  ) },
  { path: '/admin/electives/:electiveId/students', element: (
    <RoleGuard allowedRoles={['admin', 'instructor']}>
      <EnrolledStudentsPage />
    </RoleGuard>
  ) },
  { path: '/admin/electives', element: (
    <RoleGuard allowedRoles={['admin', 'instructor']}>
      <ElectiveManagement />
    </RoleGuard>
  ) },
  { path: '/admin/pricing', element: (
    <RoleGuard allowedRoles={['admin']}>
      <CoursePricing />
    </RoleGuard>
  ) },
  { path: '/admin/promo-codes', element: (
    <RoleGuard allowedRoles={['admin']}>
      <PromoCodeManagement />
    </RoleGuard>
  ) },
  { path: '/admin/bulk-pricing', element: (
    <RoleGuard allowedRoles={['admin']}>
      <BulkPricingManagement />
    </RoleGuard>
  ) },
  { path: '/admin/additional-products', element: (
    <RoleGuard allowedRoles={['admin']}>
      <AdditionalProductsManagement />
    </RoleGuard>
  ) },
  { path: '/admin/purchase-lookup', element: (
    <RoleGuard allowedRoles={['admin']}>
      <PurchaseSearch />
    </RoleGuard>
  ) },
  { path: '/admin/reports', element: (
    <RoleGuard allowedRoles={['admin']}>
      <EnrollmentReports />
    </RoleGuard>
  ) },
  { path: '/admin/reports/student-grades', element: (
    <RoleGuard allowedRoles={['admin', 'instructor']}>
      <StudentGradesReport />
    </RoleGuard>
  ) },
  { path: '/extensions/new', element: (
    <AuthGuard>
      <ExtensionRequestWizard />
    </AuthGuard>
  ) },
  { path: '/extensions/action', element: <ExtensionActionLanding /> },
  { path: '/admin/extensions', element: (
    <RoleGuard allowedRoles={['admin', 'instructor']}>
      <AdminExtensions />
    </RoleGuard>
  ) },
  { path: '/admin/attendance', element: (
    <AuthGuard>
      <AttendanceManagement />
    </AuthGuard>
  ) },
  { path: '/admin/attendance-corrections', element: (
    <RoleGuard allowedRoles={['admin']}>
      <AdminAttendanceCorrections />
    </RoleGuard>
  ) },
  { path: '/admin/deleted-items', element: (
    <RoleGuard allowedRoles={['admin']}>
      <DeletedItemsManagement />
    </RoleGuard>
  ) },
  { path: '/admin/import-export', element: (
    <RoleGuard allowedRoles={['admin']}>
      <ImportExport />
    </RoleGuard>
  ) },
  { path: '/admin/permissions', element: (
    <RoleGuard allowedRoles={['admin']}>
      <AdminPermissions />
    </RoleGuard>
  ) },
  { path: '/admin/announcements', element: (
    <RoleGuard allowedRoles={['admin']}>
      <AdminAnnouncements />
    </RoleGuard>
  ) },
  { path: '/admin/student-messages', element: (
    <RoleGuard allowedRoles={['admin']}>
      <StudentMessagesAdmin />
    </RoleGuard>
  ) },
  { path: '/admin/tools/hls-backfill', element: (
    <RoleGuard allowedRoles={['admin']}>
      <HlsBackfillPage />
    </RoleGuard>
  ) },
  { path: '/admin/support-tickets', element: (
    <RoleGuard allowedRoles={['admin', 'instructor']}>
      <AdminSupportTicketList />
    </RoleGuard>
  ) },
  { path: '/admin/support-tickets/:ticketId', element: (
    <RoleGuard allowedRoles={['admin', 'instructor']}>
      <AdminSupportTicketDetails />
    </RoleGuard>
  ) },
];

export default adminRoutes;
